// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { useEffect, useState, type ReactElement } from 'react';
import { AlertTriangle } from 'lucide-react';

// Uygulama geneli ONAY diyaloğu — native window.confirm() YERİNE. Native confirm tarayıcıyı KİLİTLER
// (otomasyonu dondurur) ve uygulamanın stilize UX'iyle uyumsuzdur. Promise tabanlı: çağır, cevabı bekle.
//
//   if (!(await confirmDialog('Silinsin mi?', { danger: true }))) return;
//
// Tek <ConfirmHost/> App kökünde mount edilir; confirmDialog() ona köprüdür.

interface ConfirmOptions {
  readonly title?: string;
  readonly confirmText?: string;
  readonly cancelText?: string;
  readonly danger?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  readonly message: string;
  readonly resolve: (ok: boolean) => void;
}

let pushConfirm: ((c: PendingConfirm) => void) | null = null;

// METIN ISTEMI — native window.prompt() YERINE. Ayni gerekce: native pencere tarayiciyi
// kilitler, bazi baglamlarda HIC ACILMAZ ve cagiran taraf sessizce iptal saniyor. Ters kayit
// sebebi bu yolla sorulur (14.09.2026: silme "sessizce" calismiyordu — sebep buydu).
interface PendingPrompt {
  readonly message: string;
  readonly placeholder?: string;
  readonly danger?: boolean;
  readonly title?: string;
  readonly confirmText?: string;
  /** İsteğe bağlı alan (ör. irsaliye yanıtı notu): boş bırakılınca "" döner, onay düğmesi pasif olmaz. */
  readonly allowEmpty?: boolean;
  readonly resolve: (deger: string | null) => void;
}
let pushPrompt: ((p: PendingPrompt) => void) | null = null;

/** Stilize metin istemi; kullanici onaylarsa girilen metin, iptal ederse null doner. */
export function promptDialog(
  message: string,
  options: { placeholder?: string; danger?: boolean; title?: string; confirmText?: string; allowEmpty?: boolean } = {},
): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    if (!pushPrompt) { resolve(null); return; }
    pushPrompt({ ...options, message, resolve });
  });
}

/** Stilize onay diyaloğu gösterir; kullanıcı onaylarsa true, iptal/kapatırsa false döner. */
export function confirmDialog(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (!pushConfirm) {
      // Host mount edilmemişse (teorik) — sessiz reddetme yerine güvenli tarafta kal: onaylama.
      resolve(false);
      return;
    }
    pushConfirm({ ...options, message, resolve });
  });
}

export function ConfirmHost(): ReactElement | null {
  const [current, setCurrent] = useState<PendingConfirm | null>(null);
  const [istem, setIstem] = useState<PendingPrompt | null>(null);
  const [istemDeger, setIstemDeger] = useState('');

  useEffect(() => {
    pushConfirm = (c) => setCurrent(c);
    pushPrompt = (p) => { setIstemDeger(''); setIstem(p); };
    return () => { pushConfirm = null; pushPrompt = null; };
  }, []);

  useEffect(() => {
    if (!current) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') answer(false);
      if (e.key === 'Enter') answer(true);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  function answer(ok: boolean): void {
    current?.resolve(ok);
    setCurrent(null);
  }

  if (istem) {
    const bitir = (deger: string | null): void => { istem.resolve(deger); setIstem(null); };
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={() => bitir(null)} aria-hidden="true" />
        <div className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
          <div className="p-5">
            {istem.title && <h2 className="text-[15px] font-bold text-foreground mb-1">{istem.title}</h2>}
            <p className={`text-[14px] mb-3 ${istem.title ? 'text-muted-foreground' : 'text-foreground'}`}>{istem.message}</p>
            <input
              autoFocus
              value={istemDeger}
              placeholder={istem.placeholder}
              onChange={(e) => setIstemDeger(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (istem.allowEmpty || istemDeger.trim() !== '')) bitir(istemDeger.trim());
                if (e.key === 'Escape') bitir(null);
              }}
              className="w-full px-3 py-2 text-[14px] rounded-lg border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-muted/30">
            <button type="button" onClick={() => bitir(null)}
              className="px-4 py-2 rounded-lg border border-border text-[14px] font-medium text-muted-foreground hover:bg-muted/50">
              Vazgeç
            </button>
            <button type="button" disabled={!istem.allowEmpty && istemDeger.trim() === ''} onClick={() => bitir(istemDeger.trim())}
              className={`px-4 py-2 rounded-lg text-[14px] font-medium disabled:opacity-50 ${
                istem.danger ? 'bg-destructive text-white hover:brightness-110' : 'bg-primary text-primary-foreground'}`}>
              {istem.confirmText ?? 'Tamam'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!current) return null;
  const danger = current.danger ?? false;

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center p-4"
      role="dialog" aria-modal="true">
      {/* Karartma scrim'i tema yüzeyi değildir → inline (tema-token bekçisi yüzey sanmasın) */}
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.4)' }}
        onClick={() => answer(false)} aria-hidden="true" />
      <div className="relative w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 p-5">
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
            danger ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
            <AlertTriangle className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 className="text-[15px] font-bold text-foreground">{current.title ?? 'Onay'}</h2>
            <p className="mt-1 text-[14px] text-muted-foreground whitespace-pre-wrap break-words">{current.message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-5 py-3 bg-muted/30">
          <button type="button" onClick={() => answer(false)}
            className="rounded-lg border border-border px-4 py-2 text-[14px] font-semibold text-muted-foreground hover:bg-muted">
            {current.cancelText ?? 'Vazgeç'}
          </button>
          <button type="button" autoFocus onClick={() => answer(true)}
            className={`rounded-lg px-4 py-2 text-[14px] font-semibold text-white ${
              danger ? 'bg-destructive hover:brightness-110' : 'bg-primary hover:brightness-110'}`}>
            {current.confirmText ?? (danger ? 'Sil' : 'Onayla')}
          </button>
        </div>
      </div>
    </div>
  );
}
